import { api } from "shared/redux/api"
import { camelizeKeys } from "shared/utils/camelize"
import { BOOK_INVENTORY_TAGS } from "./procurementItems"

// D — Goods Receipt Note. Backed by pro.goods_receipt + pro.goods_receipt_line:
// receipt is recorded per PO, per line, by quantity + quality, and does NOT
// drive the PO state machine. Whoever can see the order can confirm/amend.
const BASE = "/private/procurement/v1"
const unwrap = (response) => camelizeKeys(response?.data)

const procurementGrnApi = api.injectEndpoints({
  endpoints: (build) => ({
    // GET /purchases/{id}/grn — list of the PO's GRNs (one per delivery batch);
    // each line's receivedQty is that batch's own quantity, not cumulative.
    getPurchaseGrn: build.query({
      query: (purchaseId) => ({ url: `${BASE}/purchases/${purchaseId}/grn` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, purchaseId) => [{ type: "GRN", id: purchaseId }],
    }),
    // GET /purchases/{id}/goods-receipts — receipt history for the drawer:
    // GRNs (kind "receipt", with lines) interleaved with events (rejected,
    // procurement_receive, completed — no lines). Key rows by `key`: an event
    // id and a GRN id can be the same number.
    getPurchaseGoodsReceipts: build.query({
      query: (purchaseId) => ({ url: `${BASE}/purchases/${purchaseId}/goods-receipts` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, purchaseId) => [{ type: "GRN", id: purchaseId }],
    }),
    // POST /purchases/{id}/grn — record one more delivery batch (procurement
    // enters it directly, no OR). Recomputes the PO's delivery_status.
    createPurchaseGrn: build.mutation({
      query: ({ purchaseId, note, lines }) => ({
        url: `${BASE}/purchases/${purchaseId}/grn`,
        method: "POST",
        body: { note: note || undefined, lines },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { purchaseId }) => [
        { type: "GRN", id: purchaseId },
        { type: "PURCHASE", id: purchaseId },
        ...BOOK_INVENTORY_TAGS,
      ],
    }),
    // PUT /grn/{grnId} — amend an existing receipt.
    updateGrn: build.mutation({
      query: ({ grnId, purchaseId, lines }) => ({
        url: `${BASE}/grn/${grnId}`,
        method: "PUT",
        body: { lines },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { purchaseId }) =>
        purchaseId
          ? [{ type: "GRN", id: purchaseId }, { type: "PURCHASE", id: purchaseId }, ...BOOK_INVENTORY_TAGS]
          : BOOK_INVENTORY_TAGS,
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetPurchaseGrnQuery,
  useGetPurchaseGoodsReceiptsQuery,
  useCreatePurchaseGrnMutation,
  useUpdateGrnMutation,
} = procurementGrnApi
